import React, { useEffect, useState } from 'react';
import { Alert, Button, Col, Form, Row, Table } from 'react-bootstrap';
import Select from 'react-select';
import DatePicker from 'react-datepicker';
import {
  addWeekdayPriceChangeRule,
  getWeekdayPriceChangeRulesDataAsync,
  removeWeekdayPriceChangeRule,
  selectWeekdayPriceChangeRuleItemsData,
  selectWeekdayPriceChangeRuleItemsDataLoadingStatus,
  updateDefaultRateWeekdayPriceChangeRulesDataAsync,
  updateWeekdayPriceChangeRule,
} from '../defaultRateWeekdayRulesSlice';
import { useAppDispatch, useAppSelector } from '../../../app/hooks';
import { WeekdayPriceChangeRuleItem } from '../../../common/types/rates.types';
import { ActionButtons } from '../../../common/components';
import { PRICE_CHANGE_TYPES, WEEKDAYS_SHORT } from '../../../common/constants';
import WeekdayPriceChangeRulesForm from './WeekdayPriceChangeRulesForm';
import { useAuthUser } from '../../auth/authHooks';
import { copyWeekdayPriceChangeRulesToSeasons } from '../../defaultRateTools/defaultRateToolsSlice';
import { formatDateToFinnish } from '../../../common/utils/dateUtils';
import SeasonRatePickerContainer from '../../seasonRates/SeasonRatePickerContainer';

function DefaultRateWeekdayPriceChangeRules({
  defaultRateId,
  accommodationId,
  hasWriteAccess,
}: {
  defaultRateId: string;
  accommodationId: string;
  hasWriteAccess: boolean;
}) {
  const dispatch = useAppDispatch();
  const { roleClaims } = useAuthUser();

  const weekdayPriceChangeRuleItemsData = useAppSelector(
    selectWeekdayPriceChangeRuleItemsData,
  );
  const weekdayPriceChangeRuleItemsDataStatus = useAppSelector(
    selectWeekdayPriceChangeRuleItemsDataLoadingStatus,
  );

  const initialData: WeekdayPriceChangeRuleItem = {
    weekday: -1,
    priceChangeType: 0,
    difference: 0,
    percent: 0,
    isActive: true,
    isCopyable: true,
  };

  const weekdayOptions = WEEKDAYS_SHORT.map(item => {
    return {
      value: item.weekdayInt,
      label: item.weekdayString,
    };
  });

  const [selectedWeekday, setSelectedWeekday] = useState(-1);
  const [data, setData] = useState(initialData);
  const [submitted, setSubmitted] = useState(false);
  const [copyStartDate, setCopyStartDate] = useState<Date | null>(null);
  const [copyEndDate, setCopyEndDate] = useState<Date | null>(null);
  const [copied, setCopied] = useState<boolean>(false);
  const [selectedSeasonRateIds, setSelectedSeasonRateIds] = useState<string[]>(
    [],
  );

  useEffect(() => {
    dispatch(getWeekdayPriceChangeRulesDataAsync(defaultRateId));
  }, [defaultRateId, dispatch]);

  const handleInputChange = event => {
    const path = event.target.name.split('.');
    const finalProp = path.pop();
    const newData = { ...data };
    let pointer = newData;
    path.forEach(el => {
      pointer[el] = { ...pointer[el] };
      pointer = pointer[el];
    });
    pointer[finalProp] =
      event.target.type === 'checkbox'
        ? event.target.checked
        : event.target.value;
    setData(newData);
  };

  const handleSelectChange = (selectedOption, name) => {
    const path = name.split('.');
    const finalProp = path.pop();
    const newData = { ...data };
    let pointer = newData;
    path.forEach(el => {
      pointer[el] = { ...pointer[el] };
      pointer = pointer[el];
    });
    pointer[finalProp] = selectedOption.value;
    setData(newData);
  };

  const handleWeekdayChange = selectedOption => {
    setSelectedWeekday(selectedOption.value);

    const existingRule =
      weekdayPriceChangeRuleItemsData?.weekdayPriceChanges?.find(
        rule => rule?.weekday === selectedOption.value,
      );

    if (existingRule && existingRule.weekday) {
      setData(existingRule);
    } else {
      const newData = initialData;
      newData.weekday = selectedOption.value;
      setData(newData);
    }
  };

  const addOrUpdateRule = () => {
    const existingRule =
      weekdayPriceChangeRuleItemsData?.weekdayPriceChanges?.find(
        rule => rule?.weekday === data.weekday,
      );

    if (!existingRule) {
      dispatch(addWeekdayPriceChangeRule(data));
    } else {
      dispatch(updateWeekdayPriceChangeRule(data));
    }
  };

  const removeRule = () => {
    const existingRule =
      weekdayPriceChangeRuleItemsData?.weekdayPriceChanges?.find(
        rule => rule?.weekday === data.weekday,
      );

    if (existingRule) {
      dispatch(removeWeekdayPriceChangeRule(existingRule.weekday));
    }
  };

  const onSubmit = () => {
    dispatch(
      updateDefaultRateWeekdayPriceChangeRulesDataAsync(
        weekdayPriceChangeRuleItemsData,
      ),
    );
    setSubmitted(true);
  };

  const handleCopyingForSeasonRates = async () => {
    if (selectedSeasonRateIds.length > 0) {
      dispatch(
        copyWeekdayPriceChangeRulesToSeasons({
          accommodationId,
          // copyStartDate: formatDateToFinnish(copyStartDate),
          // copyEndDate: formatDateToFinnish(copyEndDate),
          selectedSeasonRateIds,
          weekdayPriceChanges:
            weekdayPriceChangeRuleItemsData?.weekdayPriceChanges,
        }),
      )
        .unwrap()
        .then(() => {
          setCopied(true);
        });
    }
  };

  const handleSeasonRateIds = (selectedIds: string[]) => {
    setSelectedSeasonRateIds(selectedIds);
  };

  const renderCurrentRules = (
    <>
      {WEEKDAYS_SHORT.map(weekdayItem => {
        const existingRule =
          weekdayPriceChangeRuleItemsData?.weekdayPriceChanges?.find(
            rule => rule?.weekday === weekdayItem.weekdayInt,
          );

        if (!existingRule) {
          return null;
        }

        const priceChangeType = PRICE_CHANGE_TYPES.find(
          type => type.priceChangeType === existingRule.priceChangeType,
        );

        return (
          <tr key={`weekdayPriceChangeRule-${weekdayItem.weekdayInt}`}>
            <td>
              <strong>{weekdayItem.weekdayString}</strong>
            </td>
            <td>{priceChangeType.description}</td>
            <td>
              {existingRule.priceChangeType === 1
                ? existingRule.difference
                : existingRule.percent}
            </td>
            <td>{existingRule.isCopyable ? 'Kyllä' : 'Ei'}</td>
            <td>{existingRule.isActive ? 'Kyllä' : 'Ei'}</td>
          </tr>
        );
      })}
    </>
  );

  return (
    <>
      {hasWriteAccess && (
        <Form.Group className="mb-24">
          <Form.Label>Viikonpäivä</Form.Label>
          <Select
            inputId="weekday"
            name="weekday"
            placeholder="Valitse viikonpäivä"
            isClearable
            options={weekdayOptions}
            onChange={option => handleWeekdayChange(option)}
            value={weekdayOptions.filter(
              option => option.value === selectedWeekday,
            )}
            classNames={{
              control: state => 'form-control form-control-react-select',
            }}
            isDisabled={!hasWriteAccess}
          />
        </Form.Group>
      )}

      {selectedWeekday > -1 && hasWriteAccess && (
        <>
          <Row sm={1}>
            <Row sm={1}>
              <Col>
                <Form.Label>Valitse kohdekaudet</Form.Label>
              </Col>
            </Row>
            <Col>
              <SeasonRatePickerContainer
                selectedAccommodationId={accommodationId}
                onSelectedIdsChange={handleSeasonRateIds}
              />
            </Col>
          </Row>
          <WeekdayPriceChangeRulesForm
            addOrUpdateRule={addOrUpdateRule}
            removeRule={removeRule}
            handleInputChange={handleInputChange}
            handleSelectChange={handleSelectChange}
            selectedWeekday={selectedWeekday}
            data={data}
            hasWriteAccess={hasWriteAccess}
            isDefaultRate
          />
        </>
      )}

      <hr className="hr-light my-32" />

      <h4 className="h5 mb-24">Nykyiset hintamuutokset eri viikonpäiville</h4>
      <div className="table-responsive-lg mb-40">
        <Table bordered>
          <thead>
            <tr>
              <th>Viikonpäivä</th>
              <th>Muutoksen tyyppi</th>
              <th>Muutos</th>
              <th>Kopioitava</th>
              <th>Aktiivinen</th>
            </tr>
          </thead>
          <tbody>{renderCurrentRules}</tbody>
        </Table>
      </div>

      <ActionButtons
        HandleSave={onSubmit}
        HideSaveButton={!hasWriteAccess}
        SaveButtonText="Tallenna tehdyt muutokset"
        HideCancelButton
        DeleteButtonText=""
      />
      {submitted && (
        <div className="mt-2 pt-2">
          <Alert variant="success">Tiedot tallennettu!</Alert>
        </div>
      )}
      <Form.Group className="mb-24">
        {/*
        <Form.Label>Kopioinnin alkupäivämäärä</Form.Label>
        <div className="w-100 position-relative">
          <DatePicker locale={fi}
            dateFormat="dd.MM.yyyy"
            showYearDropdown
            onChange={date => setCopyStartDate(date)}
            className="form-control"
            selected={copyStartDate}
          />
          <Button
            variant="link"
            className="pe-none position-absolute end-0 top-0"
            id="button-addon2"
          >
            <span className="bi bi-calendar2-event" />
          </Button>
        </div>
        <Form.Label>Kopioinnin päättymispäivämäärä</Form.Label>
        <div className="w-100 position-relative">
          <DatePicker locale={fi}
            dateFormat="dd.MM.yyyy"
            showYearDropdown
            onChange={date => setCopyEndDate(date)}
            className="form-control"
            selected={copyEndDate}
          />
          <Button
            variant="link"
            className="pe-none position-absolute end-0 top-0"
            id="button-addon2"
          >
            <span className="bi bi-calendar2-event" />
          </Button>
        </div>
        <Form.Text muted>
          Kohdistuu kausiin joiden alkupäivä on ennen kopioinnin
          päättymispäivämäärää sekä loppupäivä on kopioinnin alkamispäivämäärän
          jälkeen.
        </Form.Text>
        */}
        <Button
          variant="outline-blue"
          className="mt-16 mb-16 w-100"
          onClick={handleCopyingForSeasonRates}
        >
          Kopioi sääntö hintakausille
        </Button>
        {copied && (
          <div className="mt-2 pt-2">
            <Alert variant="success">Sääntö kopioitu hintakausille!</Alert>
          </div>
        )}
      </Form.Group>
    </>
  );
}

export default DefaultRateWeekdayPriceChangeRules;
